import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  TextField,
  Button,
  useMediaQuery,
  Fade,
  Typography,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RoomIcon from "@mui/icons-material/Room";
import { useNavigate } from "react-router-dom";
import Support from "../../Image/customer-service.png";

export const HEADER_HEIGHT = 40;
export const SCROLL_HIDE_THRESHOLD = 40;

const HeaderActions = (props) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery("(max-width:768px)");
  const modalRef = useRef(null);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  // Collapse the utility bar out of view once the page scrolls, so the
  // main nav can slide up and take its place.
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > SCROLL_HIDE_THRESHOLD);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close modal on outside click
  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setOpen(false);
    }
  };
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handleMessageChange = (event) => {
    setMessage(event.target.value);
  };

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handlePhoneClick = () => {
    if (window.innerWidth <= 768) {
      window.location.href = "tel:9237301652";
    } else {
      alert("Please use your phone to make the call.");
    }
  };

  const handleWhatsAppClick = () => {
    window.open("https://wa.me/9237301652", "_blank");
  };

  const handleSupportClick = () => {
    navigate("/support");
  };

  const handleEmailClick = () => {
    setOpen(!open);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        px: { xs: 1.5, sm: 3, md: 4 },
        backgroundColor: "#05080a",
        color: "white",
        position: "sticky",
        top: 0,
        zIndex: 9000,
        height: scrolled ? "0px" : `${HEADER_HEIGHT}px`,
        opacity: scrolled ? 0 : 1,
        overflow: "hidden",
        transition: "height 0.3s ease, opacity 0.2s ease",
      }}
    >
      {/* Left: phone + location */}
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, sm: 3.5 }, minWidth: 0 }}>
        <Box
          onClick={handlePhoneClick}
          sx={{ display: "flex", alignItems: "center", gap: 0.75, cursor: "pointer" }}
        >
          <PhoneIcon sx={{ fontSize: 16, color: "#5b9bd5" }} />
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, whiteSpace: "nowrap" }}>
            +91 92373 01652
          </Typography>
        </Box>

        {!isSmallScreen && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <RoomIcon sx={{ fontSize: 16, opacity: 0.85 }} />
            <Typography sx={{ fontSize: "0.8rem", opacity: 0.9, whiteSpace: "nowrap" }}>
              Bhubaneswar, Odisha
            </Typography>
          </Box>
        )}
      </Box>

      {/* Right: hours + quick actions */}
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2.5 } }}>
        {!isSmallScreen && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <AccessTimeIcon sx={{ fontSize: 16, opacity: 0.85 }} />
            <Typography sx={{ fontSize: "0.8rem", opacity: 0.9, whiteSpace: "nowrap" }}>
              Mon - Sun: 09:00 AM - 08:00 PM
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            "& .MuiIconButton-root": {
              width: 30,
              height: 30,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            },
          }}
        >
          <IconButton onClick={handleSupportClick} aria-label="Support">
            <img
              src={Support}
              alt="Support"
              style={{ height: 16, width: 16, opacity: 0.9 }}
            />
          </IconButton>
          <IconButton onClick={handleEmailClick} aria-label="Email Us">
            <MailOutlineIcon sx={{ color: "white", fontSize: 18, opacity: 0.9 }} />
          </IconButton>
          <IconButton onClick={handleWhatsAppClick} aria-label="Chat on WhatsApp">
            <WhatsAppIcon sx={{ color: "#25D366", fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {open && (
        <Fade in={open}>
          <Box
            ref={modalRef}
            sx={{
              position: "absolute",
              top: isSmallScreen ? "20%" : 55,
              right: isSmallScreen ? "10%" : 20,
              width: isSmallScreen ? "80%" : 300,
              backgroundColor: "rgba(11, 28, 44, 0.95)",
              borderRadius: 2,
              p: 2,
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.35)",
              color: "white",
              paddingTop: "20px",
              zIndex: 1000,
              height: isSmallScreen ? "auto" : 400,
              backdropFilter: "blur(10px)",
            }}
          >
            <TextField
              fullWidth
              onChange={handleEmailChange}
              placeholder="Type your email ID"
              variant="outlined"
              size="small"
              sx={{
                fontSize: "18px",
                backgroundColor: "white",
                borderRadius: 2,
                marginBottom: 4,
                "& input": {
                  fontSize: "14px",
                },
              }}
            />
            <TextField
              fullWidth
              placeholder="Type your message..."
              multiline
              rows={isSmallScreen ? 4 : 9}
              variant="outlined"
              size="small"
              onChange={handleMessageChange}
              sx={{
                fontSize: "18px",
                backgroundColor: "white",
                borderRadius: 2,
                marginBottom: 4,
                "& textarea": {
                  fontSize: "14px",
                },
              }}
            />
            <Button
              onClick={() => {
                props.AddContactForm(email, message);
                setMessage("");
                setEmail("");
              }}
              variant="contained"
              fullWidth
              sx={{
                fontSize: "18px",
                backgroundColor: "#D62E31",
                color: "white",
                "&:hover": {
                  backgroundColor: "#b5262a",
                },
              }}
            >
              Send
            </Button>
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default HeaderActions;
